import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Pin, PinOff, Save, Search, Trash2, UserRound, X } from "lucide-react";
import { useAppContext } from "../context/AppContext";
import { apiFetch, queryString } from "../lib/api";
import { formatMinutes } from "../lib/domain";
import { downloadSchedulePdf } from "../lib/pdfExport";
import { getCourseSectionColorStyle } from "../lib/scheduleColors";
import type { Paginated, SavedScheduleRecord, SectionListItem } from "../shared/schemas";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../components/ui";
import { TimetableElementsTable } from "../components/TimetableElementsTable";

const days = [
  ["M", "Monday"],
  ["T", "Tuesday"],
  ["W", "Wednesday"],
  ["R", "Thursday"],
  ["F", "Friday"],
] as const;
const startHour = 8;
const endHour = 22;
const hourHeight = 80;
const timetableStart = startHour * 60;
const timetableEnd = endHour * 60;
const timetableHeight = (endHour - startHour) * hourHeight;
const primaryScheduleTypeCodes = ["LEC", "LAB", "TUT", "THS", "SEM", "WRK"] as const;

const scheduleTypes: Record<string, { label: string; block: string; badge: string }> = {
  LEC: { label: "Lecture", block: "border-blue-300 bg-blue-100 text-blue-950", badge: "bg-blue-100 text-blue-800" },
  LAB: { label: "Lab", block: "border-emerald-300 bg-emerald-100 text-emerald-950", badge: "bg-emerald-100 text-emerald-800" },
  TUT: { label: "Tutorial", block: "border-violet-300 bg-violet-100 text-violet-950", badge: "bg-violet-100 text-violet-800" },
  THS: { label: "Thesis", block: "border-amber-300 bg-amber-100 text-amber-950", badge: "bg-amber-100 text-amber-800" },
  SEM: { label: "Seminar", block: "border-cyan-300 bg-cyan-100 text-cyan-950", badge: "bg-cyan-100 text-cyan-800" },
  WRK: { label: "Work term", block: "border-slate-300 bg-slate-100 text-slate-950", badge: "bg-slate-200 text-slate-800" },
};

interface TimetableEntry {
  section: SectionListItem;
  meeting: SectionListItem["meetings"][number];
  pinned: boolean;
}

interface PositionedEntry extends TimetableEntry {
  lane: number;
  laneCount: number;
}

function scheduleTypeDetails(scheduleType: string) {
  const code = scheduleType.trim().toUpperCase() || "OTHER";
  return {
    code,
    ...(scheduleTypes[code] ?? {
      label: code === "OTHER" ? "Other" : code,
      block: "border-indigo-300 bg-indigo-100 text-indigo-950",
      badge: "bg-indigo-100 text-indigo-800",
    }),
  };
}

function scheduleTypeFilterKey(scheduleType: string): string {
  const code = scheduleTypeDetails(scheduleType).code;
  return scheduleTypes[code] ? code : "OTHER";
}

function matchesRoomFilter(meetingRoom: string, roomFilter: string): boolean {
  const normalizedRoomFilter = roomFilter.trim().toLowerCase();
  return !normalizedRoomFilter || meetingRoom.toLowerCase().includes(normalizedRoomFilter);
}

function positionDayEntries(entries: TimetableEntry[]): PositionedEntry[] {
  const sorted = [...entries].sort(
    (left, right) =>
      left.meeting.startMinutes - right.meeting.startMinutes ||
      left.meeting.endMinutes - right.meeting.endMinutes,
  );
  const positioned: PositionedEntry[] = [];
  let cluster: Array<TimetableEntry & { lane: number }> = [];
  let clusterEnd = -1;
  let laneEnds: number[] = [];

  function finishCluster() {
    const laneCount = Math.max(laneEnds.length, 1);
    positioned.push(...cluster.map((entry) => ({ ...entry, laneCount })));
    cluster = [];
    clusterEnd = -1;
    laneEnds = [];
  }

  for (const entry of sorted) {
    if (cluster.length > 0 && entry.meeting.startMinutes >= clusterEnd) finishCluster();
    let lane = laneEnds.findIndex((endMinutes) => endMinutes <= entry.meeting.startMinutes);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(entry.meeting.endMinutes);
    } else {
      laneEnds[lane] = entry.meeting.endMinutes;
    }
    cluster.push({ ...entry, lane });
    clusterEnd = Math.max(clusterEnd, entry.meeting.endMinutes);
  }
  if (cluster.length > 0) finishCluster();
  return positioned;
}

export function ScheduleBuilderPage() {
  const { snapshotTermId, departmentId } = useAppContext();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [instructor, setInstructor] = useState("");
  const [room, setRoom] = useState("");
  const [visibleScheduleTypes, setVisibleScheduleTypes] = useState<Set<string>>(
    () => new Set([...primaryScheduleTypeCodes, "OTHER"]),
  );
  const [visibleDays, setVisibleDays] = useState<Set<string>>(
    () => new Set(days.map(([day]) => day)),
  );
  const [pinnedSectionIds, setPinnedSectionIds] = useState<Set<string>>(() => new Set());
  const [selectedScheduleId, setSelectedScheduleId] = useState("");
  const [scheduleName, setScheduleName] = useState("");
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);

  const savedSchedulesQuery = useQuery({
    queryKey: ["saved-schedules", snapshotTermId],
    queryFn: () =>
      apiFetch<SavedScheduleRecord[]>(
        `/saved-schedules${queryString({ snapshotTermId })}`,
      ),
    enabled: Boolean(snapshotTermId),
  });
  const sectionsQuery = useQuery({
    queryKey: ["schedule-builder-sections", snapshotTermId, departmentId, search, instructor, room],
    queryFn: () =>
      apiFetch<Paginated<SectionListItem>>(
        `/sections${queryString({
          snapshotTermId,
          department: departmentId,
          pageSize: 100,
          search,
          instructor,
          room,
        })}`,
      ),
    enabled: Boolean(snapshotTermId),
    placeholderData: (previous) => previous,
  });
  const pinnedQuery = useQuery({
    queryKey: ["schedule-builder-pinned", snapshotTermId, [...pinnedSectionIds].sort().join(",")],
    queryFn: () =>
      apiFetch<Paginated<SectionListItem>>(
        `/sections${queryString({
          snapshotTermId,
          pageSize: Math.max(100, pinnedSectionIds.size),
          sectionIds: [...pinnedSectionIds].join(","),
        })}`,
      ),
    enabled: Boolean(snapshotTermId && pinnedSectionIds.size > 0),
    placeholderData: (previous) => previous,
  });
  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = JSON.stringify({
        snapshotTermId,
        name: scheduleName.trim(),
        pinnedSectionIds: [...pinnedSectionIds],
      });
      if (selectedScheduleId) {
        return apiFetch<{ id: string }>(`/saved-schedules/${selectedScheduleId}`, {
          method: "PUT",
          body,
        });
      }
      return apiFetch<{ id: string }>("/saved-schedules", { method: "POST", body });
    },
    onSuccess: async (result) => {
      setSelectedScheduleId(result.id);
      setShowPinnedOnly(true);
      await queryClient.invalidateQueries({ queryKey: ["saved-schedules", snapshotTermId] });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/saved-schedules/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      setSelectedScheduleId("");
      setScheduleName("");
      setPinnedSectionIds(new Set());
      setShowPinnedOnly(false);
      await queryClient.invalidateQueries({ queryKey: ["saved-schedules", snapshotTermId] });
    },
  });

  const sections = useMemo(() => {
    const byId = new Map<string, SectionListItem>();
    for (const section of sectionsQuery.data?.items ?? []) byId.set(section.id, section);
    for (const section of pinnedQuery.data?.items ?? []) {
      if (pinnedSectionIds.has(section.id)) byId.set(section.id, section);
    }
    return [...byId.values()];
  }, [pinnedQuery.data?.items, pinnedSectionIds, sectionsQuery.data?.items]);
  const visibleDayList = days.filter(([day]) => visibleDays.has(day));
  const entries = sections.flatMap((section) =>
    section.meetings.map((meeting) => ({
      section,
      meeting,
      pinned: pinnedSectionIds.has(section.id),
    })),
  );
  const visibleEntries = entries.filter(
    ({ section, meeting, pinned }) =>
      visibleDays.has(meeting.dayCode) &&
      (pinned || matchesRoomFilter(meeting.room, room)) &&
      (!selectedScheduleId || !showPinnedOnly || pinned) &&
      visibleScheduleTypes.has(scheduleTypeFilterKey(section.scheduleType)),
  );
  const meetingsByDay = useMemo(
    () =>
      new Map(
        visibleDayList.map(([day]) => [
          day,
          positionDayEntries(
            visibleEntries.filter(
              ({ meeting }) =>
                meeting.dayCode === day &&
                meeting.startMinutes < timetableEnd &&
                meeting.endMinutes > timetableStart,
            ),
          ),
        ]),
      ),
    [visibleDayList, visibleEntries],
  );

  function toggleScheduleType(code: string) {
    setVisibleScheduleTypes((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function toggleDay(day: string) {
    setVisibleDays((current) => {
      const next = new Set(current);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  }

  function togglePin(sectionId: string) {
    setPinnedSectionIds((current) => {
      const next = new Set(current);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }

  function pinAllVisible() {
    setPinnedSectionIds((current) => {
      const next = new Set(current);
      for (const { section } of visibleEntries) next.add(section.id);
      return next;
    });
  }

  function exportPdf() {
    const name = scheduleName.trim() || "Unsaved schedule";
    downloadSchedulePdf({
      title: `Schedule Builder - ${name}`,
      subtitle: selectedScheduleId
        ? "Selected saved schedule with current filters"
        : "Unsaved schedule with current filters",
      fileName: `schedule-builder-${name}.pdf`,
      days: visibleDayList.map(([code, label]) => ({ code, label })),
      startHour,
      endHour,
      entries: [...meetingsByDay.values()].flatMap((dayEntries) =>
        dayEntries.map(({ section, meeting, lane, laneCount, pinned }) => ({
          dayCode: meeting.dayCode,
          subject: section.subject,
          courseNumber: section.courseNumber,
          sectionCode: section.sectionCode,
          sectionTitle: section.sectionTitle,
          scheduleType: section.scheduleType,
          room: meeting.room,
          facultyName: section.facultyName,
          startMinutes: meeting.startMinutes,
          endMinutes: meeting.endMinutes,
          lane,
          laneCount,
          pinned,
        })),
      ),
    });
  }

  function selectSavedSchedule(id: string) {
    setSelectedScheduleId(id);
    if (!id) {
      setScheduleName("");
      setPinnedSectionIds(new Set());
      setShowPinnedOnly(false);
      return;
    }
    const saved = savedSchedulesQuery.data?.find((schedule) => schedule.id === id);
    if (saved) {
      setScheduleName(saved.name);
      setPinnedSectionIds(new Set(saved.pinnedSectionIds));
      setShowPinnedOnly(true);
    }
  }

  const hasFilters = Boolean(search || instructor || room);

  useEffect(() => {
    if (!selectedScheduleId) return;
    const saved = savedSchedulesQuery.data?.find((schedule) => schedule.id === selectedScheduleId);
    if (!saved) return;
    setScheduleName(saved.name);
    setPinnedSectionIds(new Set(saved.pinnedSectionIds));
    setShowPinnedOnly(true);
  }, [savedSchedulesQuery.data, selectedScheduleId]);

  if (sectionsQuery.isLoading && !sectionsQuery.data) return <LoadingState />;
  if (sectionsQuery.error) return <ErrorState error={sectionsQuery.error} />;

  return (
    <>
      <PageHeader
        title="Schedule Builder"
        description="Pin timetable blocks to build and save a named working schedule."
      />
      <div className="panel space-y-4 p-4">
        <div className="grid gap-3 xl:grid-cols-[1fr_1fr_200px_auto]">
          <label className="relative">
            <span className="sr-only">Search courses</span>
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input className="field w-full pl-9" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Courses, CRNs, titles, or instructors separated by commas or semicolons" />
          </label>
          <label className="relative">
            <span className="sr-only">Filter by instructor</span>
            <UserRound className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input className="field w-full pl-9" value={instructor} onChange={(event) => setInstructor(event.target.value)} placeholder="Instructor name" />
          </label>
          <input className="field w-full" value={room} onChange={(event) => setRoom(event.target.value)} placeholder="Room" />
          <button className="button-secondary" type="button" disabled={!hasFilters} onClick={() => { setSearch(""); setInstructor(""); setRoom(""); }}>
            <X className="h-4 w-4" />
            Clear
          </button>
        </div>

        <div className="grid gap-3 xl:grid-cols-[260px_1fr_auto_auto_auto_auto]">
          <select className="field" value={selectedScheduleId} onChange={(event) => selectSavedSchedule(event.target.value)}>
            <option value="">New unsaved schedule</option>
            {(savedSchedulesQuery.data ?? []).map((schedule) => (
              <option key={schedule.id} value={schedule.id}>{schedule.name}</option>
            ))}
          </select>
          <input className="field" value={scheduleName} onChange={(event) => setScheduleName(event.target.value)} placeholder="Schedule name" />
          <button className="button-secondary" type="button" disabled={visibleEntries.length === 0} onClick={pinAllVisible}>
            <Pin className="h-4 w-4" />
            Pin all visible
          </button>
          <button className="button-primary" type="button" disabled={!scheduleName.trim() || saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            <Save className="h-4 w-4" />
            Save pins
          </button>
          <button className="button-secondary" type="button" disabled={visibleEntries.length === 0} onClick={exportPdf}>
            <Download className="h-4 w-4" />
            Export PDF
          </button>
          <button className="button-secondary text-rose-600" type="button" disabled={!selectedScheduleId || deleteMutation.isPending} onClick={() => deleteMutation.mutate(selectedScheduleId)}>
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="font-bold uppercase tracking-wider text-slate-400">Schedule type</span>
          {[...primaryScheduleTypeCodes, "OTHER"].map((code) => {
            const type = scheduleTypeDetails(code);
            const enabled = visibleScheduleTypes.has(code);
            return (
              <button key={code} className={`badge ${type.badge} transition ${enabled ? "" : "opacity-35 ring-1 ring-slate-300"}`} type="button" onClick={() => toggleScheduleType(code)} aria-pressed={enabled}>
                {code} - {type.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="font-bold uppercase tracking-wider text-slate-400">Days</span>
          {days.map(([day, label]) => {
            const enabled = visibleDays.has(day);
            return (
              <button key={day} className={`rounded-full border px-3 py-1 font-bold transition ${enabled ? "border-brand-300 bg-brand-50 text-brand-700" : "border-slate-200 bg-white text-slate-400"}`} type="button" onClick={() => toggleDay(day)} aria-pressed={enabled}>
                {label}
              </button>
            );
          })}
        </div>
        {selectedScheduleId && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-bold uppercase tracking-wider text-slate-400">Visibility</span>
            <button
              className={`rounded-full border px-3 py-1 font-bold transition ${showPinnedOnly ? "border-brand-300 bg-brand-50 text-brand-700" : "border-slate-200 bg-white text-slate-500"}`}
              type="button"
              onClick={() => setShowPinnedOnly(true)}
              aria-pressed={showPinnedOnly}
            >
              Pinned only
            </button>
            <button
              className={`rounded-full border px-3 py-1 font-bold transition ${!showPinnedOnly ? "border-brand-300 bg-brand-50 text-brand-700" : "border-slate-200 bg-white text-slate-500"}`}
              type="button"
              onClick={() => setShowPinnedOnly(false)}
              aria-pressed={!showPinnedOnly}
            >
              Pinned and unpinned
            </button>
          </div>
        )}
        <p className="text-xs text-slate-500">
          {pinnedSectionIds.size} pinned section{pinnedSectionIds.size === 1 ? "" : "s"}.
          {selectedScheduleId && showPinnedOnly ? " Showing only pinned sections from the selected saved schedule." : " Pinned sections remain visible when search filters change; schedule type and day toggles still apply."}
        </p>
      </div>

      {visibleEntries.length === 0 ? (
        <EmptyState title="No visible meetings" description="Search for sections and pin the blocks you want to keep in this schedule." />
      ) : (
        <>
          <div className="panel overflow-x-auto p-4">
            <div
              className="grid"
              style={{
                minWidth: 80 + visibleDayList.length * 170,
                gridTemplateColumns: `80px repeat(${visibleDayList.length}, minmax(170px, 1fr))`,
              }}
            >
              <div className="border-b border-r border-slate-200 bg-slate-50 p-3 text-xs font-bold uppercase text-slate-400">Time</div>
              {visibleDayList.map(([, label]) => <div key={label} className="border-b border-r border-slate-200 bg-slate-50 p-3 text-center text-xs font-bold uppercase text-slate-600">{label}</div>)}
              <div className="border-r border-slate-200">
                {Array.from({ length: endHour - startHour }, (_, index) => startHour + index).map((hour) => (
                  <div className="h-20 border-b border-slate-200 p-2 text-center text-xs font-semibold text-slate-400" key={hour}>
                    {String(hour).padStart(2, "0")}:00
                  </div>
                ))}
              </div>
              {visibleDayList.map(([day]) => (
                <div key={day} className="relative border-r border-slate-200" style={{ height: timetableHeight, backgroundImage: "repeating-linear-gradient(to bottom, transparent 0, transparent 79px, rgb(226 232 240) 79px, rgb(226 232 240) 80px)" }}>
                  {(meetingsByDay.get(day) ?? []).map(({ section, meeting, lane, laneCount, pinned }) => {
                    const scheduleType = scheduleTypeDetails(section.scheduleType);
                    const sectionColorStyle = getCourseSectionColorStyle(section);
                    const visibleStart = Math.max(meeting.startMinutes, timetableStart);
                    const visibleEnd = Math.min(meeting.endMinutes, timetableEnd);
                    const top = ((visibleStart - timetableStart) / 60) * hourHeight;
                    const height = Math.max(((visibleEnd - visibleStart) / 60) * hourHeight, 30);
                    const laneWidth = 100 / laneCount;
                    return (
                      <div
                        key={meeting.id}
                        className={`absolute overflow-hidden rounded-md border px-2 py-1 text-left text-[10px] shadow-sm ${pinned ? "ring-2 ring-brand-500" : ""}`}
                        style={{
                          ...sectionColorStyle,
                          top,
                          height,
                          left: `calc(${lane * laneWidth}% + 3px)`,
                          width: `calc(${laneWidth}% - 6px)`,
                        }}
                        title={`${section.sectionTitle} - ${section.facultyName}`}
                      >
                        <button className="absolute right-1 top-1 rounded bg-white/80 p-1 text-slate-700 shadow-sm hover:bg-white" type="button" onClick={() => togglePin(section.id)} aria-label={pinned ? `Unpin ${section.crn}` : `Pin ${section.crn}`}>
                          {pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                        </button>
                        <p className="mr-6 truncate font-bold">{section.subject} {section.courseNumber}-{section.sectionCode}</p>
                        <p className="mr-6 truncate font-bold">Sec {section.sectionCode} - {scheduleType.code}</p>
                        <p>{formatMinutes(meeting.startMinutes)}-{formatMinutes(meeting.endMinutes)}</p>
                        <p className="truncate">{meeting.room}</p>
                        <p className="truncate opacity-75">{section.facultyName}</p>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            {(sectionsQuery.data?.total ?? 0) > 100 && (
              <p className="mt-4 text-xs text-amber-700">The builder displays the first 100 matching sections plus all pinned sections. Narrow the filters to reduce the working set.</p>
            )}
          </div>
          <TimetableElementsTable
            title="Schedule Builder Elements"
            description="Table view of the currently visible meetings. Use the pin action to update this saved schedule draft."
            rows={visibleEntries}
            onTogglePin={togglePin}
          />
        </>
      )}
    </>
  );
}
